import {
  fetchWalkingRoute,
  type WalkingRoute,
} from "./routing";
import type { Coordinates, EvaluatedToilet } from "./types";

const MAX_PREFETCH_CANDIDATES = 3;

export interface WalkingRouteEstimate extends WalkingRoute {
  walkingMinutes: number;
}

export type WalkingRouteEstimates = Readonly<
  Record<string, WalkingRouteEstimate>
>;

export type RouteFetcher = (
  origin: Coordinates,
  destination: Coordinates,
  options?: { signal?: AbortSignal },
) => Promise<WalkingRoute>;

export interface PrefetchRouteEstimateOptions {
  fetchRoute?: RouteFetcher;
  signal?: AbortSignal;
}

const hasNavigableCoordinates = ({
  latitude,
  longitude,
}: Coordinates) =>
  Number.isFinite(latitude) &&
  latitude >= -90 &&
  latitude <= 90 &&
  Number.isFinite(longitude) &&
  longitude >= -180 &&
  longitude <= 180;

const isNavigable = (item: EvaluatedToilet) =>
  item.state !== "likely_unavailable" &&
  item.state !== "unavailable_confirmed" &&
  hasNavigableCoordinates(item.coordinates);

/**
 * Selects the first three unique destinations that the general-user UI may route
 * to. The input order is the current recommendation order and is never mutated.
 */
export function selectRouteEstimateCandidates(
  items: readonly EvaluatedToilet[],
): EvaluatedToilet[] {
  const selected: EvaluatedToilet[] = [];
  const selectedIds = new Set<string>();

  for (const item of items) {
    if (!isNavigable(item) || selectedIds.has(item.id)) continue;
    selected.push(item);
    selectedIds.add(item.id);
    if (selected.length === MAX_PREFETCH_CANDIDATES) break;
  }

  return selected;
}

const isAbortError = (error: unknown) =>
  error instanceof Error && error.name === "AbortError";

const createAbortGate = (signal: AbortSignal) => {
  let rejectWithReason: () => void = () => {};
  const promise = new Promise<never>((_resolve, reject) => {
    rejectWithReason = () => {
      reject(
        signal.reason instanceof Error
          ? signal.reason
          : new DOMException("The operation was aborted", "AbortError"),
      );
    };

    if (signal.aborted) {
      rejectWithReason();
      return;
    }
    signal.addEventListener("abort", rejectWithReason, { once: true });
  });
  return {
    promise,
    dispose: () => signal.removeEventListener("abort", rejectWithReason),
  };
};

const walkingMinutesFor = (route: WalkingRoute) => {
  if (
    route.kind !== "walking" ||
    !Number.isFinite(route.distanceMeters) ||
    route.distanceMeters < 0 ||
    !Number.isFinite(route.durationSeconds) ||
    route.durationSeconds < 0
  ) {
    throw new Error("invalid_route_estimate");
  }
  return Math.max(1, Math.ceil(route.durationSeconds / 60));
};

/**
 * Resolves route-backed estimates for at most the top three navigable candidates.
 * Ordinary provider failures are isolated per candidate. Aborting rejects the
 * operation so a caller cannot accidentally apply estimates for a stale origin.
 */
export async function prefetchRouteEstimates(
  items: readonly EvaluatedToilet[],
  origin: Coordinates,
  options: PrefetchRouteEstimateOptions = {},
): Promise<WalkingRouteEstimates> {
  const { fetchRoute = fetchWalkingRoute, signal } = options;
  signal?.throwIfAborted();

  const requests = selectRouteEstimateCandidates(items).map(async (item) => {
    try {
      const route = await fetchRoute(origin, item.coordinates, { signal });
      return {
        id: item.id,
        estimate: {
          ...route,
          walkingMinutes: walkingMinutesFor(route),
        } satisfies WalkingRouteEstimate,
      };
    } catch (error) {
      if (signal?.aborted || isAbortError(error)) throw error;
      return undefined;
    }
  });

  const settledPromise = Promise.allSettled(requests);
  const abortGate = signal ? createAbortGate(signal) : undefined;
  let settled: Awaited<typeof settledPromise>;
  try {
    settled = abortGate
      ? await Promise.race([settledPromise, abortGate.promise])
      : await settledPromise;
  } finally {
    abortGate?.dispose();
  }

  const aborted = settled.find(
    (result): result is PromiseRejectedResult =>
      result.status === "rejected" && isAbortError(result.reason),
  );
  if (aborted) throw aborted.reason;

  const estimates: Record<string, WalkingRouteEstimate> = {};
  for (const result of settled) {
    if (result.status !== "fulfilled" || !result.value) continue;
    estimates[result.value.id] = result.value.estimate;
  }
  return estimates;
}

/**
 * Replaces straight-line metrics where a route estimate succeeded. Candidates
 * without an estimate retain both their object identity and their stable order.
 */
export function applyRouteEstimates(
  items: readonly EvaluatedToilet[],
  estimates: WalkingRouteEstimates,
): EvaluatedToilet[] {
  return items.map((item) => {
    const estimate = estimates[item.id];
    if (!estimate) return item;

    const travelTimeDelta = estimate.walkingMinutes - item.walkingMinutes;
    return {
      ...item,
      distanceMeters: estimate.distanceMeters,
      walkingMinutes: estimate.walkingMinutes,
      expectedCost: item.expectedCost + travelTimeDelta,
    };
  });
}
